import { useId } from 'react';
import '../training.css';

const WIDTH = 480;
const HEIGHT = 220;
const PADDING_LEFT = 32;
const PADDING_RIGHT = 8;
const PADDING_TOP = 16;
const PADDING_BOTTOM = 28;
const BAR_GAP = 10;

/**
 * Dependency-free responsive bar chart (spec "Progreso"): one bar per
 * `{ label, value }`, an optional dashed `target` reference line (e.g. días
 * de entrenamiento objetivo por semana), brand colors from `training.css`,
 * and a visually-hidden `<table>` carrying the same numbers for screen
 * readers and anyone who wants the raw data. The `<svg>` is the accessible
 * "image" (`role="img"` + `aria-labelledby` pointing at a visible caption),
 * so a screen reader announces the title once, not the caption AND the svg.
 */
export function BarChart({ data = [], title, unit, target }) {
  const titleId = useId();
  const chartWidth = WIDTH - PADDING_LEFT - PADDING_RIGHT;
  const chartHeight = HEIGHT - PADDING_TOP - PADDING_BOTTOM;
  const values = data.map((item) => (typeof item.value === 'number' ? item.value : 0));
  const maxValue = Math.max(1, ...values, typeof target === 'number' ? target : 0);
  const slot = data.length > 0 ? chartWidth / data.length : 0;
  const barWidth = data.length > 0 ? Math.max(6, slot - BAR_GAP) : 0;

  function valueY(value) {
    const ratio = maxValue === 0 ? 0 : value / maxValue;
    return PADDING_TOP + chartHeight * (1 - ratio);
  }

  const baselineY = PADDING_TOP + chartHeight;
  const targetY = typeof target === 'number' ? valueY(target) : null;

  return (
    <figure className="gym-chart">
      <figcaption id={titleId} className="gym-chart__title">{title}</figcaption>
      <svg
        className="gym-chart__svg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        width="100%"
        role="img"
        aria-labelledby={titleId}
      >
        <title>{title}</title>
        <line x1={PADDING_LEFT} y1={baselineY} x2={WIDTH - PADDING_RIGHT} y2={baselineY} className="gym-chart__axis" />
        {targetY !== null && (
          <line x1={PADDING_LEFT} y1={targetY} x2={WIDTH - PADDING_RIGHT} y2={targetY} className="gym-chart__target-line" />
        )}
        {data.map((item, index) => {
          const x = PADDING_LEFT + index * slot + (slot - barWidth) / 2;
          const y = valueY(item.value);
          const height = Math.max(0, baselineY - y);
          return (
            <g key={`${item.label}-${index}`}>
              <rect x={x} y={y} width={barWidth} height={height} rx="3" className="gym-chart__bar" />
              <text x={x + barWidth / 2} y={baselineY + 16} className="gym-chart__label" textAnchor="middle">{item.label}</text>
              <text x={x + barWidth / 2} y={Math.max(y - 4, PADDING_TOP + 10)} className="gym-chart__value" textAnchor="middle">{item.value}</text>
            </g>
          );
        })}
      </svg>
      <table className="gym-sr-only">
        <caption>{title}{unit ? ` (${unit})` : ''}</caption>
        <thead>
          <tr>
            <th scope="col">Etiqueta</th>
            <th scope="col">Valor{unit ? ` (${unit})` : ''}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={`${item.label}-${index}`}>
              <td>{item.label}</td>
              <td>{item.value}</td>
            </tr>
          ))}
          {typeof target === 'number' && (
            <tr>
              <td>Objetivo</td>
              <td>{target}</td>
            </tr>
          )}
        </tbody>
      </table>
    </figure>
  );
}
